import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AppPlatformService } from '../../core/services/app-platform.service';
import { MediaImageCacheService } from '../../core/services/media-image-cache.service';
import {
  buildMediaBudgetLadder,
  buildResponsiveCategorySources,
  buildResponsiveHeroSources,
  buildResponsiveGridSources,
  isRawRasterUploadUrl,
  isWideMediaProfile,
  isBannerWideProfile,
  MEDIA_HERO_LCP_WIDTH,
  MEDIA_HERO_MAX_WIDTH,
  MEDIA_LAZY_SLOT_MAX_WIDTH,
  MEDIA_MAX_BYTES,
  MediaImageProfile,
  ProgressiveMediaSources,
  ResponsiveHeroSources,
  resolveProgressiveMediaSources,
} from '../../core/utils/media-image-profile.util';
import { resolveMediaUrl } from '../../core/utils/resolve-media-url.util';
import { shouldSkipProgressivePreview } from '../../core/utils/lcp-defer.util';
import {
  mediaRecoverySource,
  mediaRetryDelayMs,
  mediaRetryMaxAttempts,
  shouldAllowAnotherRecoveryRound,
} from '../../core/utils/media-retry.util';

@Directive({
  selector: 'img[appMediaSrc], video[appMediaSrc]',
  standalone: true,
})
export class MediaSrcDirective implements OnChanges, OnDestroy {
  @Input('appMediaSrc') mediaUrl: string | null | undefined = null;
  @Input() appMediaPlaceholder = '';
  @Input() appMediaProfile: MediaImageProfile = 'card';
  /** Figma slot size e.g. `600×600` — requests a width-matched variant per cell. */
  @Input() appMediaSlotHint = '';
  /** Visual-only: skip blur placeholder styling (still uses WebP variants). */
  @Input() appMediaSharp = false;
  @Input() appMediaProgressive = true;
  /** Layout sizes for wide srcset (grid cells). Empty keeps 100vw. */
  @Input() appMediaSizes = '';
  /** Upgrade immediately (LCP / above-the-fold). Also inferred from fetchpriority=high. */
  @Input() appMediaPriority = false;

  private budgetLadder: string[] = [];
  private previewUrl: string | null = null;
  private targetUrl: string | null = null;
  private originalUrl: string | null = null;
  private retryUrl: string | null = null;
  private previewLoaded = false;
  private upgraded = false;
  private inView = false;
  private destroyed = false;
  private observer: IntersectionObserver | null = null;
  private generation = 0;
  private retryAttempt = 0;
  private recoveryRound = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** After ladder + recovery rounds fail, never restart from hover/visibility noise. */
  private permanentlyFailed = false;
  private readonly onOnline = (): void => this.recoverIfPlaceholder();
  private readonly onVisibility = (): void => {
    if (typeof document !== 'undefined' && !document.hidden) {
      this.recoverIfPlaceholder();
    }
  };

  constructor(
    private readonly elementRef: ElementRef<HTMLImageElement | HTMLVideoElement>,
    private readonly mediaImageCache: MediaImageCacheService,
    private readonly platform: AppPlatformService
  ) {
    if (this.platform.isBrowser) {
      window.addEventListener('online', this.onOnline);
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['mediaUrl'] ||
      changes['appMediaPlaceholder'] ||
      changes['appMediaProfile'] ||
      changes['appMediaSlotHint'] ||
      changes['appMediaSharp'] ||
      changes['appMediaProgressive'] ||
      changes['appMediaSizes'] ||
      changes['appMediaPriority']
    ) {
      this.applySource();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearRetryTimer();
    this.disconnectObserver();
    if (this.platform.isBrowser) {
      window.removeEventListener('online', this.onOnline);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  private applySource(): void {
    const element = this.elementRef.nativeElement;
    this.ensureDataset(element);

    const primary = resolveMediaUrl(this.mediaUrl);
    const planned = this.planAppliedSrc(primary);
    if (planned && this.srcMatches(element, planned)) {
      this.adoptHydratedSource(element, primary, planned);
      return;
    }

    this.generation += 1;
    const generation = this.generation;
    this.clearRetryTimer();
    this.disconnectObserver();
    this.previewUrl = null;
    this.targetUrl = null;
    this.originalUrl = null;
    this.retryUrl = null;
    this.previewLoaded = false;
    this.upgraded = false;
    this.retryAttempt = 0;
    this.recoveryRound = 0;
    this.permanentlyFailed = false;
    this.inView = this.isPriorityImage();

    delete element.dataset['fallbackApplied'];
    delete element.dataset['budgetIdx'];
    delete element.dataset['mediaStage'];
    element.classList.remove(
      'media-src--loaded',
      'media-src--sharp',
      'media-src--priority',
      'media-src--wide',
      'media-src--upgrading'
    );
    if (this.appMediaSharp || this.isWideImage()) {
      element.classList.add('media-src--sharp');
    }
    if (this.isWideImage()) {
      element.classList.add('media-src--wide');
    }
    if (this.isPriorityImage()) {
      element.classList.add('media-src--priority');
    }
    element.classList.add('media-src--loading');

    if (!primary) {
      return;
    }
    this.originalUrl = primary;

    if (element.tagName === 'VIDEO') {
      const video = element as HTMLVideoElement;
      if (!video.preload) {
        video.preload = 'metadata';
      }
      video.src = primary;
      return;
    }

    if (this.mediaImageCache.shouldUseVariants(this.mediaUrl)) {
      const slotHint = this.appMediaSlotHint?.trim() ?? '';
      this.budgetLadder = buildMediaBudgetLadder(this.mediaUrl, {
        hintPx: slotHint || undefined,
        profile: this.appMediaProfile,
        maxWidth: this.variantMaxWidth(),
      });

      if (this.isWideImage()) {
        if (!this.applyWideSrcset(element as HTMLImageElement, slotHint)) {
          this.applyPlaceholder(element);
        }
        return;
      }

      if (this.isCatalogTileProfile() && this.appMediaSizes?.trim()) {
        if (this.applyCategorySrcset(element as HTMLImageElement)) {
          return;
        }
      }

      const sources = this.resolveVariantSources(slotHint);
      this.previewUrl = sources.previewUrl;
      this.targetUrl = sources.targetUrl;

      const skipPreview = shouldSkipProgressivePreview(this.appMediaProgressive, this.isPriorityImage());
      const firstSrc =
        !skipPreview && this.previewUrl
          ? this.previewUrl
          : this.targetUrl || this.previewUrl || this.budgetLadder[0];

      const applied = firstSrc || (this.isCatalogTileProfile() ? null : primary);
      if (!applied) {
        this.applyPlaceholder(element);
        return;
      }
      this.retryUrl = applied;
      element.src = applied;
      element.dataset['budgetIdx'] = '0';
      element.dataset['mediaStage'] =
        !skipPreview && this.previewUrl && this.previewUrl !== this.targetUrl ? 'preview' : 'target';
      element.removeAttribute('srcset');
      element.removeAttribute('sizes');

      if (element.dataset['mediaStage'] === 'preview' && this.platform.isBrowser) {
        this.observeIfNeeded(generation);
      } else {
        this.upgraded = true;
      }
      return;
    }

    this.retryUrl = primary;
    element.src = primary;
    if (this.isPriorityImage() || (primary || '').startsWith('/assets/lcp/')) {
      element.classList.remove('media-src--loading', 'media-src--upgrading');
      element.classList.add('media-src--loaded');
    }
  }

  @HostListener('error')
  onMediaError(): void {
    const element = this.asImage();
    if (!element || this.destroyed || this.permanentlyFailed) {
      return;
    }

    const stage = element.dataset['fallbackApplied'];
    const showingPlaceholder = stage === 'placeholder';

    // Aborted/stale loads after a src swap still fire error. Keep a real frame;
    // a painted placeholder must still retry the real URL.
    if (element.naturalWidth > 0 && !showingPlaceholder) {
      return;
    }

    if (showingPlaceholder) {
      this.scheduleMediaRetry(element);
      return;
    }

    if (stage === 'retry') {
      if (!this.isCatalogTileProfile()) {
        const next = this.nextRetrySrc(element);
        if (next) {
          element.dataset['fallbackApplied'] = 'original';
          element.dataset['mediaStage'] = 'original';
          element.src = next;
          return;
        }
      }
      if (this.appMediaPlaceholder) {
        element.dataset['fallbackApplied'] = 'placeholder';
        element.src = this.resolvePlaceholderSrc(this.appMediaPlaceholder);
      }
      this.scheduleMediaRetry(element);
      return;
    }

    // Category thumbs (non-progressive): skip the variant ladder so aborted
    // first paints do not queue extra requests. Retry after congestion.
    if (!this.appMediaProgressive && !this.isWideImage()) {
      if (this.appMediaPlaceholder) {
        element.dataset['fallbackApplied'] = 'placeholder';
        element.src = this.resolvePlaceholderSrc(this.appMediaPlaceholder);
      }
      this.scheduleMediaRetry(element);
      return;
    }

    if (
      element.dataset['mediaStage'] === 'preview' &&
      this.targetUrl &&
      this.targetUrl !== this.previewUrl
    ) {
      element.dataset['mediaStage'] = 'target';
      element.dataset['fallbackApplied'] = 'upgrade';
      this.upgraded = true;
      element.src = this.targetUrl;
      return;
    }

    const budgetIdx = Number(element.dataset['budgetIdx'] ?? 0) + 1;
    if (budgetIdx < this.budgetLadder.length) {
      element.dataset['budgetIdx'] = String(budgetIdx);
      element.dataset['fallbackApplied'] = 'budget';
      element.src = this.budgetLadder[budgetIdx];
      return;
    }

    if (
      !this.isCatalogTileProfile() &&
      stage !== 'original' &&
      this.originalUrl &&
      !this.isCurrentSrc(element, this.originalUrl)
    ) {
      element.dataset['fallbackApplied'] = 'original';
      element.dataset['mediaStage'] = 'original';
      element.src = this.originalUrl;
      return;
    }

    if (this.appMediaPlaceholder) {
      element.dataset['fallbackApplied'] = 'placeholder';
      element.src = this.resolvePlaceholderSrc(this.appMediaPlaceholder);
    }
    this.scheduleMediaRetry(element);
  }

  @HostListener('load')
  onMediaLoaded(): void {
    const element = this.asImage();
    if (!element) {
      return;
    }

    if (element.dataset['fallbackApplied'] === 'placeholder') {
      return;
    }

    this.clearRetryTimer();

    if (element.dataset['mediaStage'] === 'preview') {
      this.previewLoaded = true;
      element.classList.remove('media-src--loading');
      element.classList.add('media-src--loaded');
      this.tryUpgrade(this.generation);
      return;
    }

    element.classList.remove('media-src--loading', 'media-src--upgrading');
    element.classList.add('media-src--loaded');

    const src = element.currentSrc || element.src;
    if (isRawRasterUploadUrl(src)) {
      void this.downgradeIfOverBudget(element);
      return;
    }

    if (src.includes('/api/media/image')) {
      void this.downgradeIfOverBudget(element);
    }
  }

  private tryUpgrade(generation: number): void {
    if (
      this.destroyed ||
      generation !== this.generation ||
      !this.appMediaProgressive ||
      this.upgraded ||
      !this.previewLoaded ||
      !this.inView
    ) {
      return;
    }

    const target = this.targetUrl;
    if (!target || target === this.previewUrl) {
      this.upgraded = true;
      return;
    }

    this.upgraded = true;
    const element = this.asImage();
    if (!element) {
      return;
    }
    // Keep the preview painted until the new src loads (browser does not blank the box).
    element.classList.add('media-src--upgrading');
    element.dataset['mediaStage'] = 'target';
    element.src = target;
  }

  private observeIfNeeded(generation: number): void {
    if (this.inView) {
      this.tryUpgrade(generation);
      return;
    }

    if (typeof IntersectionObserver === 'undefined' || !this.platform.isBrowser) {
      this.inView = true;
      this.tryUpgrade(generation);
      return;
    }

    const element = this.elementRef.nativeElement;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        this.inView = true;
        this.tryUpgrade(generation);
        this.disconnectObserver();
      },
      { root: null, rootMargin: '240px 0px', threshold: 0.01 }
    );
    this.observer.observe(element);

    const rect = element.getBoundingClientRect();
    const margin = 240;
    if (
      this.platform.isBrowser &&
      rect.width > 0 &&
      rect.bottom > -margin &&
      rect.top < (window.innerHeight || 0) + margin
    ) {
      this.inView = true;
      this.tryUpgrade(generation);
      this.disconnectObserver();
    }
  }

  private isPriorityImage(): boolean {
    if (this.appMediaPriority) {
      return true;
    }
    return this.elementRef.nativeElement.getAttribute('fetchpriority') === 'high';
  }

  private disconnectObserver(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  /** First paint can starve later thumbs (6 connections/host). Retry after the pile-up clears. */
  private scheduleMediaRetry(element: HTMLImageElement): void {
    if (!this.platform.isBrowser || this.retryTimer || this.destroyed || this.permanentlyFailed) {
      return;
    }
    const catalog = this.isCatalogTileProfile();
    if (this.retryAttempt >= mediaRetryMaxAttempts(catalog)) {
      const beforeRound = this.recoveryRound;
      this.recoverIfPlaceholder();
      if (this.recoveryRound === beforeRound) {
        this.permanentlyFailed = true;
      }
      return;
    }
    const source = mediaRecoverySource(catalog, this.retryAttempt, !!this.originalUrl);
    const src =
      source === 'original'
        ? this.originalUrl
        : catalog
          ? this.catalogVariantRetryUrl()
          : this.retryAttempt === 0
            ? (this.retryUrl || this.originalUrl)
            : this.nextRetrySrc(element) || this.originalUrl;
    if (!src) {
      this.permanentlyFailed = true;
      return;
    }
    const generation = this.generation;
    const delay = mediaRetryDelayMs(catalog, this.retryAttempt);
    this.retryAttempt += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.destroyed || this.permanentlyFailed || generation !== this.generation) {
        return;
      }
      if (
        element.dataset['fallbackApplied'] !== 'placeholder' &&
        element.naturalWidth > 0
      ) {
        return;
      }
      element.dataset['fallbackApplied'] = source === 'original' ? 'original' : 'retry';
      element.dataset['mediaStage'] = source === 'original' ? 'original' : catalog ? 'target' : 'original';
      element.classList.add('media-src--loading');
      element.src = src;
    }, delay);
  }

  private recoverIfPlaceholder(): void {
    const element = this.asImage();
    if (!element || this.destroyed || this.permanentlyFailed) {
      return;
    }
    if (element.dataset['fallbackApplied'] !== 'placeholder') {
      return;
    }
    if (!shouldAllowAnotherRecoveryRound(this.recoveryRound)) {
      this.permanentlyFailed = true;
      return;
    }
    this.recoveryRound += 1;
    this.retryAttempt = 0;
    this.scheduleMediaRetry(element);
  }

  private isWideImage(): boolean {
    return isWideMediaProfile(this.appMediaProfile, this.appMediaSlotHint);
  }

  private variantMaxWidth(): number {
    return this.isWideImage() ? MEDIA_HERO_MAX_WIDTH : MEDIA_LAZY_SLOT_MAX_WIDTH;
  }

  private applyPlaceholder(element: HTMLImageElement | HTMLVideoElement): void {
    if (!this.appMediaPlaceholder) {
      return;
    }
    element.dataset['fallbackApplied'] = 'placeholder';
    element.src = this.resolvePlaceholderSrc(this.appMediaPlaceholder);
  }

  private applyWideSrcset(element: HTMLImageElement, slotHint: string): boolean {
    const responsive = this.wideSources(slotHint);
    const applied = responsive?.src || this.budgetLadder[0];
    const attrSrc = element.getAttribute('src') || '';
    const mediaIsBaked = (this.mediaUrl || '').startsWith('/assets/lcp/');
    const domIsBaked = attrSrc.startsWith('/assets/lcp/');

    // Bound media is still the local bake — never attach remote srcset (steals mobile LCP).
    if (mediaIsBaked) {
      const baked = String(this.mediaUrl);
      this.previewUrl = null;
      this.targetUrl = baked;
      this.retryUrl = baked;
      this.upgraded = true;
      element.src = baked;
      element.removeAttribute('srcset');
      element.removeAttribute('sizes');
      element.dataset['budgetIdx'] = '0';
      element.dataset['mediaStage'] = 'target';
      element.classList.remove('media-src--loading', 'media-src--upgrading');
      element.classList.add('media-src--loaded', 'media-src--sharp', 'media-src--wide', 'media-src--priority');
      return true;
    }

    if (!applied && !domIsBaked) {
      return false;
    }
    this.previewUrl = null;
    this.targetUrl = applied || attrSrc;
    this.retryUrl = this.targetUrl;
    this.upgraded = true;
    // SSR may already show the baked file while appMediaSrc is still the CMS upload path.
    element.src = domIsBaked ? attrSrc : (applied || attrSrc);
    if (responsive?.srcset) {
      const srcset = domIsBaked
        ? this.mergeBakedIntoSrcset(responsive.srcset, attrSrc)
        : responsive.srcset;
      element.setAttribute('srcset', srcset);
      element.setAttribute('sizes', this.resolvedWideSizes(responsive.sizes));
    } else {
      element.removeAttribute('srcset');
      element.removeAttribute('sizes');
    }
    element.dataset['budgetIdx'] = '0';
    element.dataset['mediaStage'] = 'target';
    return true;
  }

  /** Homepage category icons: sized srcset instead of a fixed 360w card download. */
  private applyCategorySrcset(element: HTMLImageElement): boolean {
    const responsive = buildResponsiveCategorySources(this.mediaUrl, {
      sizes: this.appMediaSizes?.trim() || undefined,
    });
    if (!responsive?.src) {
      return false;
    }
    this.previewUrl = null;
    this.targetUrl = responsive.src;
    this.retryUrl = responsive.src;
    this.upgraded = true;
    element.src = responsive.src;
    element.setAttribute('srcset', responsive.srcset);
    element.setAttribute('sizes', responsive.sizes);
    element.dataset['budgetIdx'] = '0';
    element.dataset['mediaStage'] = 'target';
    return true;
  }

  private mergeBakedIntoSrcset(srcset: string, baked: string): string {
    const descriptor = `${MEDIA_HERO_LCP_WIDTH}w`;
    const parts = srcset.split(',').map((part) => part.trim()).filter(Boolean);
    let replaced = false;
    const next = parts.map((part) => {
      if (part.endsWith(descriptor)) {
        replaced = true;
        return `${baked} ${descriptor}`;
      }
      return part;
    });
    if (!replaced) {
      next.unshift(`${baked} ${descriptor}`);
    }
    return next.join(', ');
  }

  private resolvedWideSizes(fallback: string): string {
    const sizes = this.appMediaSizes?.trim() ?? '';
    return sizes || fallback;
  }

  private wideSources(slotHint: string): ResponsiveHeroSources | null {
    const sizes = this.appMediaSizes?.trim() || undefined;
    if (isBannerWideProfile(this.appMediaProfile, slotHint)) {
      return buildResponsiveHeroSources(this.mediaUrl, {
        hintPx: slotHint || undefined,
      });
    }
    return buildResponsiveGridSources(this.mediaUrl, {
      hintPx: slotHint || undefined,
      sizes,
    });
  }

  private resolveVariantSources(slotHint: string): ProgressiveMediaSources {
    return resolveProgressiveMediaSources(this.mediaUrl, {
      hintPx: slotHint || undefined,
      profile: this.appMediaProfile,
      maxWidth: MEDIA_LAZY_SLOT_MAX_WIDTH,
    });
  }

  private planAppliedSrc(primary: string | null): string | null {
    if (!primary) {
      return null;
    }
    if (this.mediaImageCache.shouldUseVariants(this.mediaUrl)) {
      const slotHint = this.appMediaSlotHint?.trim() ?? '';
      if (this.isWideImage()) {
        return (
          this.wideSources(slotHint)?.src ||
          buildMediaBudgetLadder(this.mediaUrl, {
            hintPx: slotHint || undefined,
            profile: this.appMediaProfile,
            maxWidth: MEDIA_HERO_MAX_WIDTH,
          })[0] ||
          (this.isCatalogTileProfile() ? null : primary)
        );
      }
      const sources = this.resolveVariantSources(slotHint);
      const skipPreview = shouldSkipProgressivePreview(this.appMediaProgressive, this.isPriorityImage());
      const ladder = buildMediaBudgetLadder(this.mediaUrl, {
        hintPx: slotHint || undefined,
        profile: this.appMediaProfile,
        maxWidth: MEDIA_LAZY_SLOT_MAX_WIDTH,
      });
      return (
        (!skipPreview && sources.previewUrl
          ? sources.previewUrl
          : sources.targetUrl || sources.previewUrl || ladder[0]) ||
        (this.isCatalogTileProfile() ? null : primary)
      );
    }
    return primary;
  }

  private adoptHydratedSource(
    element: HTMLImageElement | HTMLVideoElement,
    primary: string | null,
    applied: string
  ): void {
    this.originalUrl = primary;
    this.retryUrl = applied;
    this.inView = this.isPriorityImage() || this.isWideImage();
    if (this.isWideImage()) {
      element.classList.add('media-src--wide', 'media-src--sharp');
      if (this.isPriorityImage()) {
        element.classList.add('media-src--priority');
      }
      this.upgraded = true;
      const slotHint = this.appMediaSlotHint?.trim() ?? '';
      const responsive = this.wideSources(slotHint);
      this.previewUrl = null;
      this.targetUrl = responsive?.src || applied;
      this.budgetLadder = buildMediaBudgetLadder(this.mediaUrl, {
        hintPx: slotHint || undefined,
        profile: this.appMediaProfile,
        maxWidth: MEDIA_HERO_MAX_WIDTH,
      });
      if (responsive?.srcset && element.tagName === 'IMG') {
        const attrSrc = element.getAttribute('src') || '';
        const mediaIsBaked = (this.mediaUrl || '').startsWith('/assets/lcp/');
        const domIsBaked = attrSrc.startsWith('/assets/lcp/');
        const baked = mediaIsBaked ? String(this.mediaUrl) : domIsBaked ? attrSrc : '';
        const srcset = baked
          ? this.mergeBakedIntoSrcset(responsive.srcset, baked)
          : responsive.srcset;
        element.setAttribute('srcset', srcset);
        element.setAttribute('sizes', this.resolvedWideSizes(responsive.sizes));
        if (baked) {
          element.src = baked;
          this.targetUrl = baked;
        }
      }
      element.classList.remove('media-src--loading');
      if (element.tagName === 'IMG') {
        element.classList.add('media-src--loaded');
      }
      return;
    }
    if (this.mediaImageCache.shouldUseVariants(this.mediaUrl)) {
      const slotHint = this.appMediaSlotHint?.trim() ?? '';
      const sources = this.resolveVariantSources(slotHint);
      this.previewUrl = sources.previewUrl;
      this.targetUrl = sources.targetUrl;
      this.budgetLadder = buildMediaBudgetLadder(this.mediaUrl, {
        hintPx: slotHint || undefined,
        profile: this.appMediaProfile,
        maxWidth: MEDIA_LAZY_SLOT_MAX_WIDTH,
      });
      const skipPreview = shouldSkipProgressivePreview(this.appMediaProgressive, this.isPriorityImage());
      const stillOnPreview =
        !skipPreview &&
        !!this.previewUrl &&
        this.previewUrl !== this.targetUrl &&
        this.srcMatches(element, this.previewUrl);
      this.upgraded = !stillOnPreview && (skipPreview || !this.previewUrl || this.previewUrl === this.targetUrl || applied === this.targetUrl);
      if (stillOnPreview && this.platform.isBrowser) {
        element.dataset['mediaStage'] = 'preview';
        element.classList.add('media-src--loading');
        element.classList.remove('media-src--loaded');
        this.previewLoaded = element.tagName === 'IMG' && (element as HTMLImageElement).naturalWidth > 0;
        this.observeIfNeeded(this.generation);
        return;
      }
    } else {
      this.upgraded = true;
    }
    element.classList.remove('media-src--loading');
    if (element.tagName === 'IMG') {
      element.classList.add('media-src--loaded');
    }
  }

  private srcMatches(element: HTMLImageElement | HTMLVideoElement, url: string): boolean {
    const current = (element as HTMLImageElement).currentSrc || element.src || '';
    const attr = element.getAttribute('src') || '';
    if (this.isWideImage() && (attr.startsWith('/assets/lcp/') || current.includes('/assets/lcp/'))) {
      return true;
    }
    if (!current || !url) {
      return false;
    }
    if (current === url || current.endsWith(url) || url.endsWith(current)) {
      return true;
    }
    try {
      const a = new URL(current, 'https://zerotechstores.com');
      const b = new URL(url, 'https://zerotechstores.com');
      return a.pathname === b.pathname && a.search === b.search;
    } catch {
      return false;
    }
  }

  /** Domino/SSR HTMLElement has no `dataset`; Angular Universal still runs ngOnChanges. */
  private ensureDataset(element: HTMLImageElement | HTMLVideoElement): void {
    if (element.dataset) {
      return;
    }
    Object.defineProperty(element, 'dataset', {
      value: {},
      configurable: true,
    });
  }

  private asImage(): HTMLImageElement | null {
    const element = this.elementRef.nativeElement;
    this.ensureDataset(element);
    return element.tagName === 'IMG' ? (element as HTMLImageElement) : null;
  }

  private isCatalogTileProfile(): boolean {
    return this.appMediaProfile === 'card' || this.appMediaProfile === 'thumb';
  }

  private catalogVariantRetryUrl(): string | null {
    return this.targetUrl || this.retryUrl || this.previewUrl;
  }

  private nextRetrySrc(element: HTMLImageElement): string | null {
    if (this.isCatalogTileProfile()) {
      return this.catalogVariantRetryUrl();
    }
    if (!this.originalUrl || this.isCurrentSrc(element, this.originalUrl)) {
      return null;
    }
    if (this.retryUrl && this.isCurrentSrc(element, this.retryUrl) && this.retryUrl !== this.originalUrl) {
      return this.originalUrl;
    }
    return this.originalUrl;
  }

  private clearRetryTimer(): void {
    if (this.retryTimer != null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private isCurrentSrc(element: HTMLImageElement, url: string): boolean {
    const current = element.currentSrc || element.src || '';
    if (!current || !url) {
      return false;
    }
    return current === url || current.endsWith(url) || url.endsWith(current);
  }

  private async downgradeIfOverBudget(element: HTMLImageElement): Promise<void> {
    const src = element.currentSrc || element.src;
    if (!src || element.dataset['fallbackApplied'] === 'placeholder') {
      return;
    }

    // Cross-origin HEAD requests need CORS and add console noise; ladder still downgrades on @error.
    if (!this.isSameOriginUrl(src)) {
      return;
    }

    try {
      const response = await fetch(src, { method: 'HEAD', cache: 'force-cache' });
      const length = Number.parseInt(response.headers.get('content-length') ?? '0', 10);
      if (!Number.isFinite(length) || length <= 0 || length <= MEDIA_MAX_BYTES) {
        return;
      }
    } catch {
      return;
    }

    const budgetIdx = Number(element.dataset['budgetIdx'] ?? 0) + 1;
    if (budgetIdx < this.budgetLadder.length) {
      element.dataset['budgetIdx'] = String(budgetIdx);
      element.dataset['fallbackApplied'] = 'budget';
      element.src = this.budgetLadder[budgetIdx];
    }
  }

  private isSameOriginUrl(url: string): boolean {
    try {
      return new URL(url, this.platform.isBrowser ? window.location.origin : 'https://zerotechstores.com').origin ===
        (this.platform.isBrowser ? window.location.origin : 'https://zerotechstores.com');
    } catch {
      return false;
    }
  }

  private resolvePlaceholderSrc(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return trimmed;
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }

    if (trimmed.startsWith('assets/')) {
      return `/${trimmed}`;
    }

    return trimmed;
  }
}
